import {ActionIcon, NumberInput, Select, Table, Text, TextInput} from '@mantine/core'
import {IconTrash} from '@tabler/icons-react'

import {
    type BilledResourceKind,
    billingTypeForRate,
    CREW_BILLING_TYPE_OPTIONS,
    REQUIREMENT_BILLING_TYPE_OPTIONS,
    type RequirementBillingType,
} from 'src/jobs/billing-types'
import type {AssetRequirementRow, WorkerRequirementRow} from 'src/jobs/types'

function toBillingType(value: string | null): RequirementBillingType | null {
    return REQUIREMENT_BILLING_TYPE_OPTIONS.find((option) => option.value === value)?.value ?? null
}

type RateCellsProps = {
    name: string
    row: Pick<WorkerRequirementRow, 'billingRate' | 'billingType' | 'rateFromJob'>
    kind: BilledResourceKind
    billingTypeOptions: {value: RequirementBillingType; label: string}[]
    errorPrefix: string
    errors: Record<string, string>
    onChange: (patch: Partial<RateCellsProps['row']>) => void
}

function RateCells({
    name,
    row,
    kind,
    billingTypeOptions,
    errorPrefix,
    errors,
    onChange,
}: RateCellsProps) {
    return (
        <>
            <Table.Td>
                <TextInput
                    aria-label={`${name} rate`}
                    placeholder="0.00"
                    leftSection="$"
                    value={row.billingRate ?? ''}
                    error={errors[`${errorPrefix}.billingRate`]}
                    onChange={(event) => {
                        const billingRate = event.currentTarget.value || null
                        onChange({
                            billingRate,
                            rateFromJob: null,
                            billingType:
                                billingRate == null
                                    ? null
                                    : billingTypeForRate(billingRate, row.billingType, kind),
                        })
                    }}
                />
                {row.rateFromJob ? (
                    <Text size="xs" c="dimmed">
                        from last job {row.rateFromJob}
                    </Text>
                ) : null}
            </Table.Td>
            <Table.Td>
                <Select
                    aria-label={`${name} billing type`}
                    data={billingTypeOptions}
                    value={row.billingType ?? null}
                    error={errors[`${errorPrefix}.billingType`]}
                    clearable
                    onChange={(value) => onChange({billingType: toBillingType(value)})}
                />
            </Table.Td>
        </>
    )
}

type LineRowProps<Row> = {
    row: Row
    index: number
    name: string
    errors: Record<string, string>
    takesRate: boolean
    rateColumns: boolean
    onChange: (patch: Partial<Row>) => void
    onRemove: () => void
}

function TypeCell({name, error}: {name: string; error: string | undefined}) {
    return (
        <Table.Td>
            <Text size="sm">{name}</Text>
            {error ? (
                <Text size="xs" c="red">
                    {error}
                </Text>
            ) : null}
        </Table.Td>
    )
}

function QuantityCell({
    name,
    quantity,
    error,
    onChange,
}: {
    name: string
    quantity: number
    error: string | undefined
    onChange: (quantity: number) => void
}) {
    return (
        <Table.Td>
            <NumberInput
                aria-label={`${name} quantity`}
                min={1}
                value={quantity}
                error={error}
                onChange={(value) => onChange(Number(value) || 1)}
            />
        </Table.Td>
    )
}

function RemoveCell({name, onRemove}: {name: string; onRemove: () => void}) {
    return (
        <Table.Td>
            <ActionIcon
                variant="subtle"
                color="red"
                aria-label={`Remove ${name}`}
                onClick={onRemove}
            >
                <IconTrash size={16} />
            </ActionIcon>
        </Table.Td>
    )
}

function NoRateCells({rateColumns}: {rateColumns: boolean}) {
    return rateColumns ? (
        <>
            <Table.Td />
            <Table.Td />
        </>
    ) : null
}

export function WorkerLineRow({
    row,
    index,
    name,
    errors,
    takesRate,
    rateColumns,
    onChange,
    onRemove,
}: LineRowProps<WorkerRequirementRow>) {
    const prefix = `workerRequirements.${index}`
    return (
        <Table.Tr>
            <TypeCell name={name} error={errors[`${prefix}.workerTypeId`]} />
            <QuantityCell
                name={name}
                quantity={row.quantity}
                error={errors[`${prefix}.quantity`]}
                onChange={(quantity) => onChange({quantity})}
            />
            {takesRate ? (
                <RateCells
                    name={name}
                    row={row}
                    kind="worker"
                    billingTypeOptions={CREW_BILLING_TYPE_OPTIONS}
                    errorPrefix={prefix}
                    errors={errors}
                    onChange={onChange}
                />
            ) : (
                <NoRateCells rateColumns={rateColumns} />
            )}
            <Table.Td>
                <NumberInput
                    aria-label={`${name} estimated hours each`}
                    placeholder="Optional"
                    min={0}
                    decimalScale={2}
                    value={row.estimatedHours ?? ''}
                    error={errors[`${prefix}.estimatedHours`]}
                    onChange={(value) =>
                        onChange({
                            estimatedHours: value === '' || value == null ? null : Number(value),
                        })
                    }
                />
            </Table.Td>
            <RemoveCell name={name} onRemove={onRemove} />
        </Table.Tr>
    )
}

export function AssetLineRow({
    row,
    index,
    name,
    errors,
    takesRate,
    rateColumns,
    isDrivenTruck,
    billingTypeOptions,
    onChange,
    onRemove,
}: LineRowProps<AssetRequirementRow> & {
    isDrivenTruck: boolean
    billingTypeOptions: {value: RequirementBillingType; label: string}[]
}) {
    const prefix = `assetRequirements.${index}`
    return (
        <Table.Tr>
            <TypeCell name={name} error={errors[`${prefix}.rentalAssetTypeId`]} />
            <QuantityCell
                name={name}
                quantity={row.quantity}
                error={errors[`${prefix}.quantity`]}
                onChange={(quantity) => onChange({quantity})}
            />
            {takesRate ? (
                <RateCells
                    name={name}
                    row={row}
                    kind={isDrivenTruck ? 'truck' : 'equipment'}
                    billingTypeOptions={billingTypeOptions}
                    errorPrefix={prefix}
                    errors={errors}
                    onChange={onChange}
                />
            ) : (
                <NoRateCells rateColumns={rateColumns} />
            )}
            <Table.Td />
            <RemoveCell name={name} onRemove={onRemove} />
        </Table.Tr>
    )
}
